/** The Friday board: every lane of one team, numbers only, big enough for a TV.
 *
 * The book's whiteboard, drawn by the system. Four things per lane — the goal, the result
 * against pace, this week's lead tally, and last week's commitments kept — and "Fridays
 * left" larger than anything else, because it is the only element that creates urgency
 * without anybody having to supply it. No editing here: this is the room's view, and it
 * refreshes itself every minute so a screen left on stays true.
 */

import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { Maximize, X } from 'lucide-react'

import { ErrorState, Loading } from '@/components/ui'
import { api } from '@/lib/api/client'
import { useTeams } from '@/features/teams/useTeams'
import { GoalLine } from '@/features/wig/WigLane'
import { HealthChip, ScoreBar, Tally } from '@/features/wig/WigParts'
import { formatNumber, towards, type WigLane } from '@/features/wig/useWig'

function Column({ lane, number }: { lane: WigLane; number: number }) {
  return (
    <section className="flex min-w-0 flex-col border-r-2 border-dashed border-border last:border-r-0">
      <header className="flex items-start gap-3 border-b border-border px-5 py-4">
        <span className="font-heading text-6xl leading-[0.8] text-[var(--brand)]">{number}</span>
        <div className="min-w-0 flex-1">
          <h2 className="font-heading text-2xl leading-tight text-fg">{lane.projectName}</h2>
          <p className="text-sm text-fg-muted">{[lane.owner.displayName, ...lane.supporters.map((s) => s.displayName)].join(' · ')}</p>
        </div>
        <span className="text-center">
          <span className="block font-heading text-5xl leading-none text-fg">{lane.fridaysLeft ?? '—'}</span>
          <span className="micro-label text-fg-muted">Fridays</span>
        </span>
      </header>

      <div className="border-b border-border px-5 py-4">
        <GoalLine lane={lane} />
        <div className="mt-2">
          <HealthChip health={lane.health} large />
        </div>
      </div>

      <div className="space-y-5 border-b border-border px-5 py-4">
        {lane.lag.map((measure) => (
          <div key={measure.id}>
            <p className="micro-label text-fg-muted">Lag · {measure.name}</p>
            <p className="font-heading text-5xl leading-tight text-fg">
              {formatNumber(measure.current)}
              <span className="text-2xl text-fg-muted"> {towards(measure)}</span>
            </p>
            <ScoreBar lag={measure} tall />
            <p className="mt-1.5 text-sm text-fg-muted">Pace: {formatNumber(measure.expected)} by today</p>
          </div>
        ))}
      </div>

      <div className="space-y-4 border-b border-border px-5 py-4">
        {lane.lead.map((measure) => (
          <div key={measure.id}>
            <p className="micro-label text-fg-muted">
              Lead · {measure.name} · {formatNumber(measure.weeklyTarget)} a week
            </p>
            <div className="mt-1.5">
              <Tally big count={measure.thisWeek} target={measure.weeklyTarget ?? 0} disabled />
            </div>
            <p className="mt-1 text-sm text-fg-muted">
              Last week {formatNumber(measure.lastWeek)}
              {measure.missedTwoWeeks ? (
                <span style={{ color: 'var(--status-amber)' }}> · short two weeks running</span>
              ) : null}
            </p>
          </div>
        ))}
      </div>

      <div className="px-5 py-4">
        <p className="micro-label text-fg-muted">Commitments kept last week</p>
        <p className="font-heading text-4xl text-fg">
          {lane.commitments.lastWeekKept}
          <span className="text-2xl text-fg-muted"> / {lane.commitments.lastWeekTotal}</span>
        </p>
      </div>
    </section>
  )
}

export function WigBoard() {
  const [params] = useSearchParams()
  const team = params.get('team') ?? undefined
  const { data: teamData } = useTeams()
  const teamLabel = teamData?.teams.find((t) => t.team === team)?.label ?? 'All teams'
  const { data, isPending, error } = useQuery({
    queryKey: ['wigs', 'board', team ?? 'all', 'friday'],
    // A screen left on in a room has to stay true without anyone touching it.
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data: body } = await api.GET('/api/v1/wigs', {
        params: { query: team ? { team: team as never } : {} },
      })
      return body as unknown as { lanes: WigLane[] }
    },
  })

  const lanes = data?.lanes ?? []
  const kept = lanes.reduce((sum, lane) => sum + lane.commitments.lastWeekKept, 0)
  const made = lanes.reduce((sum, lane) => sum + lane.commitments.lastWeekTotal, 0)
  const week = lanes[0]?.weekStart

  return (
    <div className="fixed inset-0 z-50 flex flex-col overflow-auto bg-canvas">
      <header className="flex flex-wrap items-center gap-4 border-b-2 border-[var(--fg)] px-6 py-3">
        <h1 className="font-heading text-2xl uppercase tracking-wide text-fg">{teamLabel} — WIG board</h1>
        {week ? (
          <span className="micro-label text-fg-muted">
            Week of {new Date(`${week}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'long' })}
          </span>
        ) : null}
        <span className="flex-1" />
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-fg hover:bg-surface"
          onClick={() => void document.documentElement.requestFullscreen?.()}
        >
          <Maximize size={14} aria-hidden />
          Full screen
        </button>
        <Link
          to={`/wig${team ? `?team=${team}` : ''}`}
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-fg hover:bg-surface"
        >
          <X size={14} aria-hidden />
          Leave the board
        </Link>
      </header>

      {isPending ? (
        <div className="p-8">
          <Loading label="Drawing the board" />
        </div>
      ) : error ? (
        <div className="p-8">
          <ErrorState error={error} />
        </div>
      ) : lanes.length === 0 ? (
        <p className="p-8 text-lg text-fg-muted">No WIGs for {teamLabel} yet.</p>
      ) : (
        <div
          className="grid flex-1"
          style={{ gridTemplateColumns: `repeat(${Math.min(lanes.length, 4)}, minmax(280px, 1fr))` }}
        >
          {lanes.map((lane, index) => (
            <Column key={lane.id} lane={lane} number={index + 1} />
          ))}
        </div>
      )}

      <footer className="flex flex-wrap justify-between gap-4 border-t-2 border-[var(--fg)] px-6 py-3 text-sm text-fg-muted">
        <span>
          Last week's commitments kept: <b className="text-fg">{kept} / {made}</b>
        </span>
        <span>Refreshes every minute · board review Fridays</span>
      </footer>
    </div>
  )
}
